/**
 * Escalas de classificacao dos eixos de saude (logica pura, sem React/MUI).
 *
 * Quase todos os eixos usam a escala PADRAO, monotonica no score
 * (Otimo >=75 / Bom >=50 / Suficiente >=25 / Regular <25): quanto maior, melhor.
 *
 * O C1 (Mais Acesso) e a excecao: a nota metodologica define um "U invertido",
 * em que percentual maior NAO e melhor:
 *   Otimo      > 50 e <= 70
 *   Bom        > 30 e <= 50
 *   Suficiente > 10 e <= 30
 *   Regular    <= 10 OU > 70
 * Por isso ordenar as equipes por score crescente ("pior primeiro") esta errado
 * no C1: uma equipe com 85% e Regular, mas apareceria no fim da lista.
 *
 * O backend ja resolve a classificacao correta por equipe
 * (indicadores.service.ts -> getClassificacaoC1 quando o eixo e o C1), entao aqui
 * so se ordena/pinta a partir do que ele devolveu.
 */

/** Eixos com escala de U invertido (percentual maior nao e melhor). */
export const EIXOS_ESCALA_INVERTIDA = new Set<string>(['c1-mais-acesso']);

export function isEscalaInvertida(eixo?: string): boolean {
  return !!eixo && EIXOS_ESCALA_INVERTIDA.has(eixo);
}

/** Faixa ideal (Otimo) do C1: > 50 e <= 70. */
export const C1_FAIXA_IDEAL = { min: 50, max: 70 };

/**
 * Distancia (em pontos percentuais) entre um percentual e a faixa ideal do C1.
 * Zero para quem esta dentro da faixa. Quanto maior, pior o desempenho.
 */
export function distanciaFaixaIdealC1(pct: number): number {
  if (pct < C1_FAIXA_IDEAL.min) return C1_FAIXA_IDEAL.min - pct;
  if (pct > C1_FAIXA_IDEAL.max) return pct - C1_FAIXA_IDEAL.max;
  return 0;
}

/** Ordem de severidade das classificacoes: 0 = pior. Rotulo desconhecido/'Sem dados' vai para o fim. */
const SEVERIDADE: Record<string, number> = {
  Regular: 0,
  Suficiente: 1,
  Bom: 2,
  Otimo: 3,
};

export function severidadeClassificacao(classificacao?: string): number {
  const s = classificacao ? SEVERIDADE[classificacao] : undefined;
  return s !== undefined ? s : 99;
}

/** Marcas da barra de progresso para a escala do C1 (limites das faixas). */
export const C1_THRESHOLDS = [
  { value: 10, color: '#e65100', label: 'Suficiente' },
  { value: 30, color: '#1565c0', label: 'Bom' },
  { value: 50, color: '#2e7d32', label: 'Otimo' },
  { value: 70, color: '#c62828', label: 'Limite (acima e Regular)' },
];

interface EquipeOrdenavel {
  equipe?: string;
  score?: number;
  classificacao?: string;
}

/**
 * Comparador "pior primeiro" para a lista de equipes de um eixo.
 *
 * Chave 1: severidade da classificacao devolvida pelo backend (Regular primeiro).
 * Chave 2: no C1, distancia da faixa ideal (mais longe = pior primeiro); nos demais
 *          eixos, score crescente (comportamento historico).
 * Chave 3 (so no C1): nome da equipe, para desempatar quem esta dentro da faixa ideal.
 *
 * Nos eixos que NAO sao o C1 a ordem final e identica a do score crescente: a
 * classificacao ali e funcao nao-decrescente do score, logo a chave 1 nunca
 * contradiz a chave 2 - apenas pre-agrupa itens que a chave 2 ordenaria igual.
 * Empates devolvem 0 e o Array.prototype.sort (estavel) preserva o ORDER BY do SQL.
 */
export function compararEquipesPiorPrimeiro(
  eixo?: string,
): (a: EquipeOrdenavel, b: EquipeOrdenavel) => number {
  const invertida = isEscalaInvertida(eixo);
  return (a, b) =>
    severidadeClassificacao(a?.classificacao) - severidadeClassificacao(b?.classificacao) ||
    (invertida
      ? distanciaFaixaIdealC1(b?.score ?? 0) - distanciaFaixaIdealC1(a?.score ?? 0)
      : (a?.score ?? 0) - (b?.score ?? 0)) ||
    (invertida ? String(a?.equipe ?? '').localeCompare(String(b?.equipe ?? ''), 'pt-BR') : 0);
}
